import { useEffect, useState, type KeyboardEvent } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { useAccount } from '../../context/AccountContext';
import {
  CREATE_TINYCMS_PREVIEW,
  GET_TINYCMS_APPROVALS,
  GET_TINYCMS_DATASET,
  PUBLISH_TINYCMS_CONTENT,
  RUN_TINYCMS_COPY_EDIT,
  UNPUBLISH_TINYCMS_CONTENT,
} from '../../graphql';
import type { Finding, ItemState } from '../cms/shared';
import { Loader } from '../common/PageLoader';

/**
 * The tinycms half of the article screen ("tinycms UX optimization review
 * v10.zip", Prototype v7 → Article): the publish card, checks, search &
 * sharing, dataset, Reach and approval history. Every rule — an approval names
 * the exact version, blocking findings refuse, a later edit stays off the live
 * site — is the server's; these only show its state and send the click.
 */

export interface CmsArticleState extends ItemState {
  preflight?: Finding[];
  copyEdit?: { fresh: boolean; checkedAt?: string; findings: Finding[]; required?: boolean };
}

export interface CmsArticleContent {
  title: string;
  slug: string;
  summary: string | null;
  hero: { url: string; alt: string | null } | null;
  author: string | null;
  tags: string[];
  seo: {
    metaTitle: string | null;
    metaDescription: string | null;
    canonicalUrl: string | null;
    ogImageUrl: string | null;
    noindex: boolean | null;
  };
}

export interface CmsArticle {
  id: string;
  slug: string;
  title: string;
  workingHash: string;
  state: CmsArticleState;
  content: CmsArticleContent;
  dataset: {
    name: string | null;
    recordCount: number | null;
    workingHash: string;
    hasPublishedVersion: boolean;
  } | null;
}

export const META_TITLE_MAX = 60;
export const META_DESC_MAX = 160;
const SLUG_RE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

export const isValidSlug = (slug: string) => SLUG_RE.test(slug) && slug.length <= 80;
export const isHttpUrl = (value: string) => {
  try {
    const u = new URL(value);
    return u.protocol === 'https:' || u.protocol === 'http:';
  } catch {
    return false;
  }
};

const plain = (html?: string | null) =>
  (html || '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const clip = (text: string, max: number) =>
  text.length > max ? `${text.slice(0, max - 1)}…` : text;

const when = (iso?: string | null) => (iso ? format(new Date(iso), 'MMM d, yyyy · h:mm a') : '');

/** Status line for the byline and the publish card. */
export function cmsStatus(state: CmsArticleState): { label: string; color: string } {
  const changes = Boolean(state.live) && state.hasUnpublishedChanges;
  if (state.scheduled) return { label: 'Scheduled', color: 'var(--ink-accent)' };
  if (state.takenDown && !state.live) return { label: 'Taken down', color: 'var(--ink-ink3)' };
  if (changes) return { label: 'Live · unpublished changes', color: 'var(--ink-amber)' };
  if (state.live) return { label: 'Live', color: 'var(--ink-green)' };
  return { label: 'Draft', color: 'var(--ink-ink3)' };
}

interface CheckRow {
  icon: string;
  text: string;
  sev: string;
  color: string;
}

const PASS = { icon: '✓', sev: '', color: 'var(--ink-green-ink)' };

function findingText(f: Finding) {
  if (f.quote) {
    return `“${f.quote}”${f.suggestion ? ` → ${f.suggestion}` : ''}${f.message ? ` · ${f.message}` : ''}`;
  }
  return f.message || f.code.replace(/_/g, ' ');
}

function findingRow(f: Finding, severity = f.severity): CheckRow {
  return severity === 'blocking'
    ? { icon: '✕', text: findingText(f), sev: 'Blocking', color: 'var(--ink-red)' }
    : { icon: '●', text: findingText(f), sev: 'Warning', color: 'var(--ink-amber)' };
}

/** Structure (preflight) and copy-edit results as one list, plus the blocking count. */
export function articleChecks(state: CmsArticleState): { rows: CheckRow[]; blocking: number } {
  const preflight = state.preflight || [];
  const ce = state.copyEdit || { fresh: false, findings: [] };
  const required = ce.required !== false;
  const rows: CheckRow[] = [];

  if (!ce.fresh) {
    rows.push(
      required
        ? {
            icon: '✕',
            text: 'Copy edit hasn’t run on this version',
            sev: 'Blocking',
            color: 'var(--ink-red)',
          }
        : {
            icon: '●',
            text: 'Copy edit hasn’t run on this version',
            sev: 'Optional',
            color: 'var(--ink-ink3)',
          }
    );
  } else if (!ce.findings.length) {
    rows.push({
      ...PASS,
      text: `Copy edit passed${ce.checkedAt ? ` · ${format(new Date(ce.checkedAt), 'MMM d, h:mm a')}` : ''}`,
    });
  } else {
    // An advisory copy edit never blocks: its findings read as warnings.
    for (const f of ce.findings) rows.push(findingRow(f, required ? f.severity : 'warning'));
  }

  if (preflight.length) {
    for (const f of [...preflight].sort(
      (a, b) => Number(b.severity === 'blocking') - Number(a.severity === 'blocking')
    )) {
      rows.push(findingRow(f));
    }
  } else {
    rows.push({ ...PASS, text: 'Hero alt text, required fields and links check out' });
  }

  const blocking =
    preflight.filter((f) => f.severity === 'blocking').length +
    (required && ce.fresh ? ce.findings.filter((f) => f.severity === 'blocking').length : 0) +
    (required && !ce.fresh ? 1 : 0);
  return { rows, blocking };
}

/** Open a fresh preview link of the working copy in a new tab. */
function usePreviewInTab(articleId: string) {
  const { accountId } = useAccount();
  const [create, { loading }] = useMutation(CREATE_TINYCMS_PREVIEW);
  const open = async () => {
    // Open synchronously so the popup isn't blocked, then point it at the URL.
    const win = window.open('', '_blank');
    try {
      const { data } = await create({ variables: { accountId, articleId } });
      const url = data?.createTinycmsPreview?.url;
      if (win && url) win.location.href = url;
      else win?.close();
    } catch {
      win?.close();
    }
  };
  return { open, loading };
}

const QUARTER = 15 * 60 * 1000;
const nextQuarter = () => new Date(Math.ceil((Date.now() + 60 * 1000) / QUARTER) * QUARTER);
const toLocalInput = (d: Date) => format(d, "yyyy-MM-dd'T'HH:mm");

/**
 * Status, version, Publish / Schedule / Unpublish. Clicking Publish is the
 * approval of the working version this screen shows (its hash); the server
 * refuses if it changed since, or if blocking checks remain without a reason.
 */
export function CmsPublishCard({
  article,
  editing,
  disabled,
  onChanged,
  onDelete,
  deleting,
}: {
  article: CmsArticle;
  editing: boolean;
  disabled?: boolean;
  onChanged: () => void | Promise<unknown>;
  onDelete: () => void;
  deleting: boolean;
}) {
  const { accountId } = useAccount();
  const [publish, { loading: publishing }] = useMutation(PUBLISH_TINYCMS_CONTENT);
  const [unpublish, { loading: unpublishing }] = useMutation(UNPUBLISH_TINYCMS_CONTENT);
  const preview = usePreviewInTab(article.id);
  const [override, setOverride] = useState('');
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [scheduleAt, setScheduleAt] = useState('');
  const [note, setNote] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);

  const s = article.state;
  const live = s.live?.version;
  const changes = Boolean(s.live) && s.hasUnpublishedChanges;
  const status = cmsStatus(s);
  const { blocking } = articleChecks(s);
  const canPublish = s.hasUnpublishedChanges || Boolean(s.scheduled);
  const busy = publishing || unpublishing || deleting || Boolean(disabled);
  const needsOverride = blocking > 0 && canPublish;

  const versionLine = s.scheduled
    ? `v${s.scheduled.version} scheduled${live ? ` · v${live} live` : ''}`
    : live
      ? `v${live} live`
      : 'never published';
  const pubNote = editing
    ? 'Finish editing to publish this version.'
    : s.scheduled
      ? `Goes live ${when(s.scheduled.at)}.`
      : changes
        ? `Your saved edits aren’t live yet. Readers still see v${live}.`
        : live
          ? 'Readers see the latest version.'
          : 'Not published yet. Blocking checks must pass first.';
  const publishLabel = publishing
    ? 'Publishing…'
    : s.scheduled
      ? 'Publish now'
      : !s.hasUnpublishedChanges
        ? 'Up to date'
        : live
          ? 'Publish changes'
          : 'Publish';

  const run = async (action: 'publish' | 'schedule') => {
    setNote(null);
    const scheduledFor = action === 'schedule' ? new Date(scheduleAt).toISOString() : undefined;
    const ask =
      action === 'publish'
        ? 'Publish this version now?'
        : `Schedule this version for ${when(scheduledFor)}?`;
    if (
      !window.confirm(
        `${ask}\n\nVersion ${article.workingHash.slice(0, 10)} — your approval is recorded with your name and the time.`
      )
    ) {
      return;
    }
    try {
      const { data } = await publish({
        variables: {
          accountId,
          kind: 'article',
          id: article.id,
          action,
          contentHash: article.workingHash,
          scheduledFor,
          overrideReason: override.trim() || undefined,
        },
      });
      const r = data?.publishTinycmsContent;
      if (r?.ok && r.unchanged) setNote({ tone: 'ok', text: 'Already live. Nothing to publish.' });
      else if (r?.ok) {
        setNote({
          tone: 'ok',
          text:
            action === 'publish'
              ? `Published v${r.version}.`
              : `Scheduled v${r.version} for ${when(scheduledFor)}.`,
        });
        setOverride('');
        setScheduleOpen(false);
      } else {
        setNote({
          tone: 'error',
          text: `Refused: ${r?.message || r?.refused || 'unknown reason'}`,
        });
      }
      await onChanged();
    } catch (err) {
      setNote({ tone: 'error', text: err instanceof Error ? err.message : 'Publish failed' });
    }
  };

  const takeDown = async () => {
    const reason = window.prompt('Take this off the live site? Reason (optional):');
    if (reason === null) return;
    setNote(null);
    try {
      const { data } = await unpublish({
        variables: { accountId, kind: 'article', id: article.id, reason: reason || undefined },
      });
      const r = data?.unpublishTinycmsContent;
      setNote({
        tone: 'ok',
        text: r?.unchanged ? 'Nothing was live.' : 'Unpublished. Readers no longer see it.',
      });
      await onChanged();
    } catch (err) {
      setNote({ tone: 'error', text: err instanceof Error ? err.message : 'Unpublish failed' });
    }
  };

  return (
    <div className="ink-rail-card">
      <div className="ink-rail-status" style={{ color: status.color }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
          <span className="ink-dot" style={{ background: status.color }} />
          {status.label}
        </span>
        <span className="ink-rail-kicker" style={{ whiteSpace: 'nowrap' }}>
          {versionLine}
        </span>
      </div>
      <div className="ink-rail-note">{pubNote}</div>
      {needsOverride && !editing ? (
        <input
          className="ink-input ink-override"
          value={override}
          onChange={(e) => setOverride(e.target.value)}
          placeholder="Override reason (recorded with your approval)"
          aria-label="Override reason"
        />
      ) : null}
      <div className="ink-rail-actions">
        <button
          type="button"
          className={`ink-btn ${canPublish ? 'ink-btn-primary' : ''}`}
          onClick={() => void run('publish')}
          disabled={busy || editing || !canPublish || (needsOverride && !override.trim())}
        >
          {publishLabel}
        </button>
        <button
          type="button"
          className="ink-btn"
          style={{ flex: 'none' }}
          onClick={() => {
            setScheduleOpen((o) => !o);
            if (!scheduleAt) setScheduleAt(toLocalInput(nextQuarter()));
          }}
          disabled={busy || editing}
        >
          Schedule
        </button>
      </div>
      {scheduleOpen && !editing ? (
        <div style={{ marginTop: 12 }}>
          <input
            type="datetime-local"
            step={900}
            className="ink-input"
            value={scheduleAt}
            onChange={(e) => setScheduleAt(e.target.value)}
            aria-label="Publish at"
          />
          <div className="ink-rail-actions">
            <button
              type="button"
              className="ink-btn ink-btn-primary"
              onClick={() => void run('schedule')}
              disabled={
                busy ||
                !scheduleAt ||
                new Date(scheduleAt).getTime() <= Date.now() ||
                (blocking > 0 && !override.trim())
              }
            >
              {publishing ? 'Scheduling…' : 'Confirm'}
            </button>
            <button type="button" className="ink-btn" onClick={() => setScheduleOpen(false)}>
              Cancel
            </button>
          </div>
        </div>
      ) : null}
      {note ? (
        <div
          className="ink-rail-note"
          role="status"
          style={{ color: note.tone === 'error' ? 'var(--ink-red)' : 'var(--ink-green-ink)' }}
        >
          {note.text}
        </div>
      ) : null}
      <div className="ink-rail-links">
        <button
          type="button"
          className="ink-link-btn"
          onClick={() => void preview.open()}
          disabled={preview.loading}
        >
          Preview working copy ↗
        </button>
        <span style={{ display: 'inline-flex', gap: 14 }}>
          {s.live || s.scheduled ? (
            <button
              type="button"
              className="ink-link-btn"
              style={{ color: 'var(--ink-ink3)' }}
              onClick={() => void takeDown()}
              disabled={busy}
            >
              {unpublishing ? '…' : 'Unpublish'}
            </button>
          ) : null}
          <button
            type="button"
            className="ink-link-btn"
            style={{ color: 'var(--ink-amber)' }}
            onClick={onDelete}
            disabled={busy}
          >
            {deleting ? 'Deleting…' : s.live || s.scheduled ? 'Delete' : 'Delete draft'}
          </button>
        </span>
      </div>
    </div>
  );
}

/** Structure and copy-edit results, with Run copy edit. */
export function ArticleChecks({
  article,
  editing,
  onChanged,
}: {
  article: CmsArticle;
  editing: boolean;
  onChanged: () => void | Promise<unknown>;
}) {
  const { accountId } = useAccount();
  const [copyEdit, { loading }] = useMutation(RUN_TINYCMS_COPY_EDIT);
  const [error, setError] = useState('');
  const { rows } = articleChecks(article.state);

  const runCopyEdit = async () => {
    setError('');
    try {
      await copyEdit({ variables: { accountId, id: article.id } });
      await onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Copy edit failed');
    }
  };

  return (
    <section data-part="checks">
      <div className="ink-rail-head">
        <div className="ink-rail-label" style={{ margin: 0 }}>
          Checks
        </div>
        <button
          type="button"
          className="ink-link-btn"
          onClick={() => void runCopyEdit()}
          disabled={loading || editing}
          title={editing ? 'Finish editing first' : undefined}
        >
          {loading ? 'Checking…' : 'Run copy edit'}
        </button>
      </div>
      {rows.map((c, i) => (
        <div className="ink-check" key={i}>
          <span className="ink-check-icon" style={{ color: c.color }}>
            {c.icon}
          </span>
          <span>{c.text}</span>
          <span className="ink-check-sev" style={{ color: c.color }}>
            {c.sev}
          </span>
        </div>
      ))}
      {error ? (
        <div className="ink-rail-note" style={{ color: 'var(--ink-red)' }}>
          {error}
        </div>
      ) : null}
    </section>
  );
}

export interface SeoDraft {
  slug: string;
  metaTitle: string;
  metaDescription: string;
  canonicalUrl: string;
  ogImageUrl: string;
}

/** A search result and share card, read-only; or the fields behind them. */
export function SearchSharing({
  article,
  editing,
  draft,
  onDraft,
  title,
  summary,
  heroUrl,
  siteBase,
  onToggleNoindex,
  togglingNoindex,
}: {
  article: CmsArticle;
  editing: boolean;
  draft: SeoDraft;
  onDraft: (patch: Partial<SeoDraft>) => void;
  /** The title and summary on screen (the defaults for meta title / description). */
  title: string;
  summary: string;
  heroUrl: string;
  siteBase: string;
  onToggleNoindex: () => void;
  togglingNoindex: boolean;
}) {
  const seo = article.content.seo || ({} as CmsArticleContent['seo']);
  const noindex = Boolean(seo.noindex);
  const slug = editing ? draft.slug : article.slug;
  const metaTitle = (editing ? draft.metaTitle : seo.metaTitle) || title;
  const metaDesc = (editing ? draft.metaDescription : seo.metaDescription) || plain(summary);
  const shareImage = (editing ? draft.ogImageUrl : seo.ogImageUrl) || heroUrl;
  const canonical = editing ? draft.canonicalUrl : seo.canonicalUrl;
  const slugBad = editing && !isValidSlug(draft.slug);
  const canonicalBad = editing && Boolean(draft.canonicalUrl) && !isHttpUrl(draft.canonicalUrl);
  const ogBad = editing && Boolean(draft.ogImageUrl) && !isHttpUrl(draft.ogImageUrl);

  return (
    <section data-part="seo" className="ink-detail-block">
      <div className="ink-detail-block-head">
        <h3>Search &amp; sharing</h3>
        <button
          type="button"
          className="ink-noindex"
          role="switch"
          aria-checked={noindex}
          onClick={onToggleNoindex}
          disabled={togglingNoindex}
        >
          Hide from search engines
          <span className="ink-mini-switch" data-on={noindex ? 'true' : 'false'} />
        </button>
      </div>
      {noindex ? (
        <p className="ink-form-hint" style={{ marginTop: 6 }}>
          Once published, search engines are asked not to list this article.
        </p>
      ) : null}

      {!editing ? (
        <>
          <div className="ink-serp">
            <div style={{ minWidth: 0 }}>
              <div className="ink-serp-url">
                {siteBase}
                {slug}
              </div>
              <div className="ink-serp-title">{clip(metaTitle, META_TITLE_MAX)}</div>
              <div className="ink-serp-desc">
                {clip(metaDesc, META_DESC_MAX) || 'No description.'}
              </div>
            </div>
            <div className="ink-serp-img">
              {shareImage ? <img src={shareImage} alt="" /> : <span>share image</span>}
            </div>
          </div>
          <div className="ink-serp-foot">
            <span>Canonical: {canonical || 'same as URL'}</span>
            <span>
              Share image:{' '}
              {seo.ogImageUrl ? 'custom' : heroUrl ? 'uses hero' : 'none (add a hero image)'}
            </span>
          </div>
        </>
      ) : (
        <div className="ink-form-grid">
          <label className="ink-form-full">
            <span className="ink-form-label">URL slug</span>
            <div className="ink-slug" data-bad={slugBad ? 'true' : undefined}>
              <span>{siteBase}</span>
              <input
                value={draft.slug}
                onChange={(e) => onDraft({ slug: e.target.value.toLowerCase() })}
                spellCheck={false}
              />
            </div>
            {slugBad ? (
              <span className="ink-form-hint" style={{ color: 'var(--ink-red)' }}>
                Lowercase letters and digits, separated by single hyphens.
              </span>
            ) : article.state.live && draft.slug !== article.slug ? (
              <span className="ink-form-hint">
                The old address will redirect here once you publish.
              </span>
            ) : null}
          </label>
          <label>
            <span className="ink-form-label">
              Meta title
              <span
                className="ink-form-count"
                data-over={metaTitle.length > META_TITLE_MAX ? 'true' : undefined}
              >
                {metaTitle.length} / {META_TITLE_MAX}
              </span>
            </span>
            <input
              className="ink-input"
              value={draft.metaTitle}
              placeholder={title}
              onChange={(e) => onDraft({ metaTitle: e.target.value })}
            />
          </label>
          <label>
            <span className="ink-form-label">Canonical URL</span>
            <input
              className="ink-input"
              value={draft.canonicalUrl}
              placeholder="Same as URL"
              onChange={(e) => onDraft({ canonicalUrl: e.target.value.trim() })}
              data-bad={canonicalBad ? 'true' : undefined}
            />
          </label>
          <label className="ink-form-full">
            <span className="ink-form-label">
              Meta description
              <span
                className="ink-form-count"
                data-over={metaDesc.length > META_DESC_MAX ? 'true' : undefined}
              >
                {metaDesc.length} / {META_DESC_MAX}
              </span>
            </span>
            <textarea
              className="ink-input"
              rows={2}
              value={draft.metaDescription}
              placeholder={clip(plain(summary), META_DESC_MAX) || 'Defaults to the summary'}
              onChange={(e) => onDraft({ metaDescription: e.target.value })}
            />
          </label>
          <label className="ink-form-full">
            <span className="ink-form-label">Share image URL</span>
            <input
              className="ink-input"
              value={draft.ogImageUrl}
              placeholder="Uses the hero image"
              onChange={(e) => onDraft({ ogImageUrl: e.target.value.trim() })}
              data-bad={ogBad ? 'true' : undefined}
            />
          </label>
        </div>
      )}
    </section>
  );
}

/**
 * The article's JSON dataset: its schema check, what is live, and the
 * document itself. Agents attach and change it; it publishes on its own.
 */
export function DatasetCard({ article }: { article: CmsArticle }) {
  const { accountId } = useAccount();
  const has = Boolean(article.dataset);
  const { data, loading, refetch } = useQuery(GET_TINYCMS_DATASET, {
    variables: { accountId, articleId: article.id, includeDocument: true },
    skip: !accountId || !has,
    fetchPolicy: 'cache-and-network',
  });
  const [publish, { loading: publishing }] = useMutation(PUBLISH_TINYCMS_CONTENT);
  const [note, setNote] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);
  const [showAll, setShowAll] = useState(false);
  const ds = data?.tinycmsDataset;
  const summary = article.dataset;
  const records = summary?.recordCount ?? ds?.recordCount;

  const findings: Finding[] = ds?.state?.findings || [];
  const blocking = findings.filter((f) => f.severity === 'blocking').length;
  const json = ds?.document !== undefined ? JSON.stringify(ds.document, null, 2) : '';
  const lines = json.split('\n');
  const long = lines.length > 40;

  const publishDataset = async () => {
    if (!ds) return;
    if (
      !window.confirm(
        `Publish this dataset now?\n\nVersion ${String(ds.workingHash).slice(0, 10)} — your approval is recorded with your name and the time.`
      )
    ) {
      return;
    }
    setNote(null);
    try {
      const { data: res } = await publish({
        variables: {
          accountId,
          kind: 'dataset',
          id: article.id,
          action: 'publish',
          contentHash: ds.workingHash,
        },
      });
      const r = res?.publishTinycmsContent;
      setNote(
        r?.ok
          ? { tone: 'ok', text: r.unchanged ? 'Already live.' : `Published dataset v${r.version}.` }
          : { tone: 'error', text: `Refused: ${r?.message || r?.refused}` }
      );
      await refetch();
    } catch (err) {
      setNote({ tone: 'error', text: err instanceof Error ? err.message : 'Publish failed' });
    }
  };

  const liveLabel = ds?.state?.live
    ? ds.state.hasUnpublishedChanges
      ? `v${ds.state.live.version} live · changes not live`
      : `v${ds.state.live.version} live`
    : ds?.state?.scheduled
      ? `v${ds.state.scheduled.version} scheduled`
      : 'not published';

  return (
    <section data-part="dataset" className="ink-detail-block">
      <div className="ink-detail-block-head">
        <h3>Dataset</h3>
        {has ? (
          <span className="ink-form-hint">
            {records != null ? `${records} record${records === 1 ? '' : 's'} · ` : ''}JSON ·
            published separately
          </span>
        ) : null}
      </div>
      {!has ? (
        <p className="ink-detail-empty">
          No dataset. Your agent can attach one: a JSON document with a declared schema.
        </p>
      ) : loading && !ds ? (
        <Loader label="Loading dataset" className="ink-detail-empty" />
      ) : ds ? (
        <div className="ink-ds">
          <div className="ink-ds-head">
            <span style={{ fontFamily: 'var(--ink-mono)' }}>{ds.name || 'dataset'}</span>
            {!ds.state?.checksFresh ? (
              <span style={{ color: 'var(--ink-ink3)' }}>Schema not checked</span>
            ) : findings.length ? (
              <span style={{ color: blocking ? 'var(--ink-red)' : 'var(--ink-amber)' }}>
                {findings.length} schema finding{findings.length === 1 ? '' : 's'}
              </span>
            ) : (
              <span style={{ color: 'var(--ink-green-ink)' }}>✓ Schema check passes</span>
            )}
            {ds.state?.schemaChanged ? (
              <span style={{ color: 'var(--ink-amber)' }}>Schema changed</span>
            ) : null}
            <span style={{ marginLeft: 'auto', color: 'var(--ink-ink3)', fontWeight: 400 }}>
              {liveLabel}
            </span>
            {ds.state?.hasUnpublishedChanges ? (
              <button
                type="button"
                className="ink-link-btn"
                onClick={() => void publishDataset()}
                disabled={publishing || blocking > 0}
                title={
                  blocking ? 'Blocking schema findings: ask your agent to fix them' : undefined
                }
              >
                {publishing ? 'Publishing…' : 'Publish dataset'}
              </button>
            ) : null}
          </div>
          {findings.length ? (
            <ul className="ink-ds-findings">
              {findings.map((f, i) => (
                <li
                  key={i}
                  style={{
                    color: f.severity === 'blocking' ? 'var(--ink-red)' : 'var(--ink-amber)',
                  }}
                >
                  {f.message || f.code}
                  {f.path ? <span style={{ color: 'var(--ink-ink3)' }}> · {f.path}</span> : null}
                </li>
              ))}
            </ul>
          ) : null}
          <pre className="ink-ds-json">
            {long && !showAll ? `${lines.slice(0, 40).join('\n')}\n…` : json}
          </pre>
          {long ? (
            <button
              type="button"
              className="ink-link-btn"
              style={{ margin: '0 16px 12px' }}
              onClick={() => setShowAll((v) => !v)}
            >
              {showAll ? 'Show less' : `Show all ${lines.length} lines`}
            </button>
          ) : null}
        </div>
      ) : null}
      {has ? (
        <p className="ink-form-hint" style={{ marginTop: 8 }}>
          To change the data, ask your agent.
        </p>
      ) : null}
      {note ? (
        <p
          className="ink-form-hint"
          style={{ color: note.tone === 'error' ? 'var(--ink-red)' : 'var(--ink-green-ink)' }}
        >
          {note.text}
        </p>
      ) : null}
    </section>
  );
}

/** Reach: what happens to the article on social channels. */
export function ReachCard({ reachOn }: { reachOn: boolean }) {
  return (
    <div className="ink-rail-block">
      <div className="ink-rail-head" style={{ marginBottom: 0 }}>
        <div className="ink-rail-label" style={{ margin: 0 }}>
          Reach
        </div>
        <span className="ink-rail-kicker">ADD-ON</span>
      </div>
      <div className="ink-rail-note">
        {reachOn
          ? 'When a campaign shares new articles, your agent drafts one post per channel once this goes live.'
          : 'Share new articles on your social channels: your agent drafts a post per channel when an article goes live.'}
      </div>
      <Link
        to={reachOn ? '/calendar' : '/addons'}
        className="ink-link-btn"
        style={{ display: 'inline-block', marginTop: 8, fontSize: 13.5 }}
      >
        {reachOn ? 'See posts in Reach →' : 'About Reach →'}
      </Link>
    </div>
  );
}

const ACTION_VERB: Record<string, string> = {
  publish: 'Published',
  schedule: 'Scheduled',
  unpublish: 'Taken off the site',
};

/** Publish decisions on this article, newest first. */
export function ArticleApprovals({
  articleId,
  refreshKey,
}: {
  articleId: string;
  refreshKey?: unknown;
}) {
  const { accountId } = useAccount();
  const { data, loading, refetch } = useQuery(GET_TINYCMS_APPROVALS, {
    variables: { accountId, kind: 'article', subjectId: articleId, limit: 10, offset: 0 },
    skip: !accountId,
    fetchPolicy: 'cache-and-network',
  });
  // A new key (after a publish or unpublish) refetches.
  useEffect(() => {
    if (refreshKey !== undefined && accountId) void refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);
  const rows: any[] = data?.tinycmsApprovals || [];
  if (loading && !rows.length) return <Loader label="Loading" className="ink-rail-note" />;
  if (!rows.length) return <p className="ink-rail-note">Not published yet.</p>;
  return (
    <div>
      {rows.map((r) => {
        const refused = r.outcome !== 'accepted';
        const verb = ACTION_VERB[r.action] || r.action;
        const what = refused
          ? `${verb.split(' ')[0]} refused${r.refusalReason ? `: ${String(r.refusalReason).replace(/_/g, ' ')}` : ''}`
          : `${verb}${r.versionNumber && r.action !== 'unpublish' ? ` v${r.versionNumber}` : ''}${
              r.scheduledFor ? ` for ${format(new Date(r.scheduledFor), 'MMM d, h:mm a')}` : ''
            }`;
        const who = `${r.action === 'unpublish' ? 'Asked by' : refused ? 'Tried by' : 'Approved by'} ${
          r.approvedBy
        } · ${r.via === 'mcp' ? 'agent' : 'website'}${r.overrideReason ? ` · override: ${r.overrideReason}` : ''}${
          r.note ? ` · ${r.note}` : ''
        }`;
        return (
          <div className="ink-hist-row" key={r.id}>
            <time>{r.recordedAt ? format(new Date(r.recordedAt), 'MMM d') : '—'}</time>
            <span style={refused ? { color: 'var(--ink-red)' } : undefined}>
              {what}
              <span className="ink-hist-who">{who}</span>
            </span>
          </div>
        );
      })}
    </div>
  );
}

/** Tag chips with remove, and "+ add" (Enter or comma adds). */
export function TagEditor({
  tags,
  onChange,
}: {
  tags: string[];
  onChange: (next: string[]) => void;
}) {
  const [text, setText] = useState('');
  const add = () => {
    const parts = text
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean)
      .filter((t) => !tags.includes(t));
    if (parts.length) onChange([...tags, ...parts]);
    setText('');
  };
  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      add();
    } else if (e.key === 'Backspace' && !text && tags.length) {
      onChange(tags.slice(0, -1));
    }
  };
  return (
    <span className="ink-rail-terms">
      {tags.map((t) => (
        <span className="ink-rail-chip" key={t}>
          {t}
          <button
            type="button"
            className="ink-chip-x"
            aria-label={`Remove ${t}`}
            onClick={() => onChange(tags.filter((x) => x !== t))}
          >
            ×
          </button>
        </span>
      ))}
      <input
        className="ink-tag-input"
        value={text}
        placeholder="+ add"
        aria-label="Add tag"
        onChange={(e) => setText(e.target.value)}
        onKeyDown={onKey}
        onBlur={add}
      />
    </span>
  );
}
